import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Typography } from "@mui/material";
import { Drawer } from "@outpave/ds";
import type { ApiKeyPermissionCatalogue } from "hooks/useApiKeys";
import { useUnsavedChangesGuard } from "hooks/useUnsavedChangesGuard";
import { Stack } from "components/layout/reusableLayouts";
import { isSamePermissionSet } from "utils/apiKeys/apiKeyPermissions";
import ApiKeyPermissionsField from "./ApiKeyPermissionsField";

interface ApiKeyAdvancedPermissionsDrawerProps {
  open: boolean;
  catalogue: ApiKeyPermissionCatalogue;
  selected: string[];
  // What the permissions mean for this key's API versions.
  versionHelper: string;
  onApply: (next: string[]) => void;
  onClose: () => void;
}

const secondaryText = { color: "text.secondary" } as const;

const ApiKeyAdvancedPermissionsDrawer: React.FC<
  ApiKeyAdvancedPermissionsDrawerProps
> = ({ open, catalogue, selected, versionHelper, onApply, onClose }) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<string[]>(selected);

  useEffect(() => {
    if (open) setDraft(selected);
  }, [open, selected]);

  const { requestLeave, dialog } = useUnsavedChangesGuard(
    !isSamePermissionSet(draft, selected),
    onClose,
  );
  const sensitiveLocked = catalogue.permissions.some(
    (option) => option.sensitive && !option.assignable,
  );

  return (
    <>
      <Drawer
        open={open}
        onClose={requestLeave}
        title={t("ApiKeyAdvancedPermissions")}
        footer={
          <>
            <Button variant="primary-outlined" onClick={requestLeave}>
              {t("Cancel")}
            </Button>
            <Button
              variant="primary-filled"
              onClick={() => {
                onApply(draft);
                onClose();
              }}
            >
              {t("Apply")}
            </Button>
          </>
        }
      >
        <Stack gap="var(--space-2)">
          <Typography variant="body1" sx={secondaryText}>
            {versionHelper}
          </Typography>
          <Typography variant="body1" sx={secondaryText}>
            {t("ApiKeyAdvancedPermissionsHelper")}
          </Typography>
          {sensitiveLocked && (
            <Typography variant="body1" sx={secondaryText}>
              {t("ApiKeySensitivePermissionsOwnerOnly")}
            </Typography>
          )}
        </Stack>
        <ApiKeyPermissionsField
          catalogue={catalogue}
          selected={draft}
          onSelectedChange={setDraft}
        />
      </Drawer>
      {dialog}
    </>
  );
};

export default ApiKeyAdvancedPermissionsDrawer;
